import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Chip,
  Alert,
  Avatar,
  Divider,
  useTheme
} from '@mui/material';
import {
  Person,
  Business,
  Phone,
  Language,
  Payment,
  Save
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PPartnerProfile = ({ partnerData, onUpdate }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    partner_name: partnerData.partner_name || '',
    phone: partnerData.phone || '',
    supported_currencies: partnerData.supported_currencies || [],
    payment_methods: partnerData.payment_methods || [],
    business_description: partnerData.business_description || ''
  });

  const currencies = [
    'USD', 'EUR', 'GBP', 'NGN', 'KES', 'GHS', 'ZAR', 'EGP',
    'CAD', 'AUD', 'JPY', 'CNY', 'INR', 'BRL', 'MXN'
  ];

  const paymentMethods = [
    'Bank Transfer', 'Mobile Money', 'PayPal', 'Skrill', 'Neteller',
    'Perfect Money', 'Payoneer', 'Western Union', 'MoneyGram',
    'Cash Deposit', 'Digital Wallet', 'Cryptocurrency'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Import the service dynamically
      const { default: p2pPartnerService } = await import('../../services/p2pPartner.service');
      const data = await p2pPartnerService.updateProfile(formData);

      if (data.success) {
        setSuccess('Profile updated successfully!');
        setEditing(false);
        onUpdate && onUpdate();
      } else {
        setError(data.message || 'Failed to update profile.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      partner_name: partnerData.partner_name || '',
      phone: partnerData.phone || '',
      supported_currencies: partnerData.supported_currencies || [],
      payment_methods: partnerData.payment_methods || [],
      business_description: partnerData.business_description || ''
    });
    setEditing(false);
    setError('');
    setSuccess('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'suspended':
        return 'Suspended';
      default:
        return status;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Partner Profile
            </Typography>
            {!editing && (
              <Button
                variant="outlined"
                onClick={() => setEditing(true)}
                disabled={partnerData.status !== 'approved'}
              >
                Edit Profile
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Profile Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.primary.main,
                fontSize: '2rem',
                fontWeight: 'bold',
                mr: 3
              }}
            >
              {partnerData.partner_name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {partnerData.partner_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={getStatusText(partnerData.status)}
                  color={getStatusColor(partnerData.status)}
                />
                {partnerData.is_verified && (
                  <Chip
                    label="Verified Partner"
                    color="success"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={partnerData.country}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Partner Name"
                  value={editing ? formData.partner_name : partnerData.partner_name}
                  onChange={(e) => handleInputChange('partner_name', e.target.value)}
                  variant={editing ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !editing,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={partnerData.country}
                  variant="filled"
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Contact support to change country"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editing ? formData.phone : partnerData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  variant={editing ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: !editing,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Volume"
                  value={`$${partnerData.total_volume.toLocaleString()}`}
                  variant="filled"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              {/* Supported Currencies */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Language color="primary" />
                  Supported Currencies
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {editing ? (
                  <Autocomplete
                    multiple
                    options={currencies}
                    value={formData.supported_currencies}
                    onChange={(event, value) => handleInputChange('supported_currencies', value)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          color="primary"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supported Currencies"
                        placeholder="Select currencies"
                      />
                    )}
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {partnerData.supported_currencies.map((currency) => (
                      <Chip
                        key={currency}
                        label={currency}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              {/* Payment Methods */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Payment color="primary" />
                  Payment Methods
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {editing ? (
                  <Autocomplete
                    multiple
                    options={paymentMethods}
                    value={formData.payment_methods}
                    onChange={(event, value) => handleInputChange('payment_methods', value)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          color="secondary"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Payment Methods"
                        placeholder="Select payment methods"
                      />
                    )}
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {partnerData.payment_methods.map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              {/* Business Description */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Business color="primary" />
                  Business Description
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Description"
                  value={editing ? formData.business_description : partnerData.business_description}
                  onChange={(e) => handleInputChange('business_description', e.target.value)}
                  variant={editing ? "outlined" : "filled"}
                  multiline
                  rows={4}
                  InputProps={{
                    readOnly: !editing,
                  }}
                />
              </Grid>

              {/* Action Buttons */}
              {editing && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>

          {/* Statistics Section */}
          <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Partner Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Completed Trades
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {partnerData.completed_trades}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Success Rate
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {partnerData.success_rate}%
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Average Rating
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {partnerData.average_rating}/5.0
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Total Ratings
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {partnerData.total_ratings}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default P2PPartnerProfile;
